/* WeQuote Dev Board — the Roadmap scheduling engine (PRD §5.5, roadmap-spec.md).
 * Pure port of computeSchedule + lane classifiers + roadmap mutations.
 *
 * Per-developer, sequential, WORKING-DAYS-ONLY (weekends + block-outs skipped).
 * Anchor = the lane's earliest card start; forward-only cascade; deadlines are
 * fixed inputs; pins hold their date (an earlier card overrunning a pin raises
 * a CONFLICT, never a silent shove); a still-in-design card's dev block starts
 * no earlier than the working day after its design deadline; and FRACTIONAL
 * DAY-PACKING — sub-day cards share a working day until estimates roughly
 * fill it (≈1.0) then spill, while cards of a day or more flow one-after-
 * another and never start on a part-used day.
 *
 * estCompletion is ALWAYS derived — recomputed from the same shared data on
 * every client, surfaced on cards from Design Complete onward. */
import type { Feature, Blockout } from './types'
import { DEVCOLS, DL, isPool } from './constants'
import {
  parseISO, toISO, snapWorking, nextWorkingDay, addWorkingDays,
  setScheduleBlockouts, hasEstimate, cardStatus, isLate,
} from './logic'
import type { MutResult } from './mutations'
import { todayISO } from './dates'

/* ── lane classifiers (verbatim) ── */
export function rmActive(f: Feature): boolean {
  return DEVCOLS.indexOf(f.status) > -1 || f.status === 'testing' || f.status === 'testcomplete' || f.status === 'onhold'
}
export function isPreDev(f: Feature): boolean {
  return isPool(f.status) || f.status === 'design' || f.status === 'designcomplete'
}
export function onRoadmap(f: Feature): boolean {
  // custreview (Lee, 29 Aug): waiting on the customer — off the calendar and
  // out of the scheduling flow until it moves on (it isn't dev queue time).
  return !!(f.dev && DL[f.dev] && f.status !== 'needsreview' && f.status !== 'live' && f.status !== 'archived' && f.status !== 'idea' && f.status !== 'custreview')
}
/** Placed in time: owner + estimate + a date anchor (deadline OR start —
 *  Lee, 26 Aug: a start date with a derived end must plot without a deadline). */
export function datedCard(f: Feature): boolean {
  return onRoadmap(f) && hasEstimate(f) && !!(f.deadline || f.start)
}
export function comingSoon(f: Feature): boolean {
  return onRoadmap(f) && (!f.deadline || !hasEstimate(f))
}
export function unscheduled(f: Feature): boolean {
  return isPreDev(f) && !(f.dev && DL[f.dev])
}
export function readyToSchedule(f: Feature): boolean {
  return !!(f.dev && DL[f.dev] && f.status === 'designcomplete' && !f.start)
}
export function stageRank(f: Feature): number {
  if (rmActive(f)) return 0
  if (f.status === 'designcomplete') return 1
  if (f.status === 'design') return 2
  if (isPool(f.status)) return 3
  return 9
}
export function laneSort(a: Feature, b: Feature): number {
  return stageRank(a) - stageRank(b) || a.po - b.po
}

/* ── risk reads ── */
export function daysBetween(aISO: string, bISO: string): number {
  return Math.round((parseISO(bISO).getTime() - parseISO(aISO).getTime()) / 86400000)
}
export function daysLate(f: Feature): number {
  if (!f.deadline || !f.estCompletion) return 0
  return Math.max(0, daysBetween(f.deadline, f.estCompletion))
}
export function rmAtRisk(f: Feature): boolean {
  if (!onRoadmap(f)) return false
  const c = cardStatus(f).cls
  return c === 'atrisk' || c === 'overdue'
}
export function rmRiskDetail(f: Feature): string {
  const st = cardStatus(f)
  return isLate(f) ? daysLate(f) + ' days late' : st.cls === 'overdue' ? 'deadline passed' : 'at risk'
}
export function blockDays(b: Blockout): number {
  return Math.max(1, daysBetween(b.start, b.end) + 1)
}
export function blockW(b: Blockout): number {
  const w = blockDays(b) * 7
  return Math.max(46, Math.min(240, w))
}

/* ── THE ENGINE ── */
export interface Schedule { start: Record<number, string>; conflict: Record<number, boolean> }

/** Compute the per-dev schedule and return decorated features (estCompletion
 *  set on dated cards, cleared elsewhere) + start/conflict maps. Blockouts are
 *  installed into the working-day maths for the duration of the call. `now`
 *  allows deterministic tests; production omits it. */
export function computeSchedule(features: Feature[], blockouts: Blockout[], now?: Date): Schedule & { features: Feature[] } {
  setScheduleBlockouts(blockouts)
  const start: Record<number, string> = {}
  const conflict: Record<number, boolean> = {}
  const est: Record<number, string> = {}
  const today = () => (now ? new Date(now.getTime()) : new Date())
  DEVCOLS.forEach((dev) => {
    const lane = features.filter((f) => f.dev === dev && datedCard(f) && !f.multiStage).sort(laneSort)
    // Fractional day-packing state: curDay = day being filled; dayUsed = fraction (0..1).
    let curDay: Date | null = null
    let dayUsed = 0
    lane.forEach((f, i) => {
      let startD: Date
      let fresh = false
      if (i === 0) {
        startD = f.start ? parseISO(f.start) : curDay || today()
        fresh = true
      } else if (f.pinnedStart && f.start) {
        startD = parseISO(f.start)
        if (curDay && startD < curDay) conflict[f.id] = true
        fresh = true
      } else {
        startD = curDay ? new Date(curDay.getTime()) : f.start ? parseISO(f.start) : today()
      }
      // Design-completion gate: dev block starts no earlier than the working
      // day after the design deadline while the card is still in design.
      if (f.status === 'design' && f.designDeadline) {
        const gate = nextWorkingDay(parseISO(f.designDeadline), dev)
        if (startD < gate) { startD = gate; fresh = true }
      }
      startD = snapWorking(startD, dev)
      if (fresh) { curDay = startD; dayUsed = 0 } // first / pinned / gated cards start their own day
      if (!hasEstimate(f)) { est[f.id] = ''; start[f.id] = toISO(curDay!); return }
      const dur = parseFloat(f.time)
      if (dur >= 1) {
        if (dayUsed > 0) { curDay = nextWorkingDay(curDay!, dev); dayUsed = 0 } // never split a full day onto a part-used day
        start[f.id] = toISO(curDay!)
        const endD = addWorkingDays(curDay!, dur, dev)
        est[f.id] = toISO(endD)
        curDay = nextWorkingDay(endD, dev)
        dayUsed = 0
      } else {
        if (dayUsed > 0 && dayUsed + dur > 1.0001) { curDay = nextWorkingDay(curDay!, dev); dayUsed = 0 } // spill
        start[f.id] = toISO(curDay!)
        est[f.id] = toISO(curDay!) // sub-day card finishes the day it starts
        dayUsed += dur
        if (dayUsed >= 0.9999) { curDay = nextWorkingDay(curDay!, dev); dayUsed = 0 } // day full → advance
      }
    })
  })
  /* ── v2 §2: staged cards — each stage schedules on its OWN assignee's lane,
   * starting no earlier than (a) the working day after the previous stage
   * finishes (waitsOnPrev handoff) and (b) the end of that assignee's packed
   * lane (never double-booked — the addendum's required improvement over the
   * prototype). A stage `start` pins it (conflict flagged if it overlaps). ── */
  const laneEnd: Record<string, string> = {}
  DEVCOLS.forEach((dev) => {
    features.forEach((f) => {
      if (f.dev === dev && datedCard(f) && !f.multiStage && est[f.id] && est[f.id] > (laneEnd[dev] || '')) laneEnd[dev] = est[f.id]
    })
  })
  const stageEnds: Record<string, string> = { ...laneEnd }
  const stageOut: Record<number, { schedStart: string; estCompletion: string }[]> = {}
  features.slice().sort((a, b) => a.po - b.po).forEach((f) => {
    if (!f.multiStage) return
    let prevEnd = ''
    let lastComp = ''
    stageOut[f.id] = f.stages.map((sg) => {
      const dev = sg.dev || f.dev || DEVCOLS[0]
      if (!sg.time || parseFloat(sg.time) <= 0) { return { schedStart: '', estCompletion: '' } }
      let startD: Date
      if (sg.start) {
        startD = parseISO(sg.start) // pinned
        const busyUntil = stageEnds[dev] || ''
        if ((busyUntil && sg.start <= busyUntil) || (prevEnd && sg.waitsOnPrev && sg.start <= prevEnd)) conflict[f.id] = true
      } else {
        let earliest = today()
        if (prevEnd && sg.waitsOnPrev) { const d = nextWorkingDay(parseISO(prevEnd), dev); if (d > earliest) earliest = d }
        if (stageEnds[dev]) { const d = nextWorkingDay(parseISO(stageEnds[dev]), dev); if (d > earliest) earliest = d }
        startD = earliest
      }
      startD = snapWorking(startD, dev)
      const endD = addWorkingDays(startD, parseFloat(sg.time), dev)
      const endISO = toISO(endD)
      if (!stageEnds[dev] || endISO > stageEnds[dev]) stageEnds[dev] = endISO
      prevEnd = endISO
      lastComp = endISO
      return { schedStart: toISO(startD), estCompletion: endISO }
    })
    est[f.id] = lastComp // umbrella projected finish = last stage's completion
  })
  const decorated = features.map((f) => {
    if (f.multiStage) {
      const outs = stageOut[f.id] || []
      const stages = f.stages.map((sg, i) => {
        const o = outs[i] || { schedStart: '', estCompletion: '' }
        return sg.schedStart === o.schedStart && sg.estCompletion === o.estCompletion ? sg : { ...sg, ...o }
      })
      const target = est[f.id] || ''
      return f.estCompletion === target && stages.every((sg, i) => sg === f.stages[i]) ? f : { ...f, stages, estCompletion: target }
    }
    const target = datedCard(f) ? est[f.id] || '' : ''
    return f.estCompletion === target ? f : { ...f, estCompletion: target }
  })
  return { start, conflict, features: decorated }
}

/* ── roadmap mutations (pure) ── */
export function togglePin(fs: Feature[], id: number, schedStart: Record<number, string>): MutResult {
  return {
    features: fs.map((f) => {
      if (f.id !== id) return f
      const pinnedStart = !f.pinnedStart
      const startVal = pinnedStart && !f.start && schedStart[id] ? schedStart[id] : f.start
      return { ...f, pinnedStart, start: startVal }
    }),
    changedIds: [id],
  }
}
export function retidyLane(fs: Feature[], dev: string): MutResult {
  const ids = fs.filter((f) => f.dev === dev && f.pinnedStart).map((f) => f.id)
  return {
    features: fs.map((f) => (f.dev === dev ? { ...f, pinnedStart: false } : f)),
    toast: 'Cleared pins and repacked ' + (DL[dev] || dev) + '’s lane.',
    changedIds: ids,
  }
}
export function scheduleCardToday(fs: Feature[], id: number): MutResult {
  let title = ''
  let dev = ''
  const next = fs.map((f) => {
    if (f.id !== id) return f
    title = f.title
    dev = f.dev
    return { ...f, start: todayISO() }
  })
  return { features: next, toast: '“' + title + '” scheduled onto ' + (DL[dev] || 'the') + '’s lane.', changedIds: [id] }
}
export function acknowledgeRisk(fs: Feature[], id: number): MutResult {
  return { features: fs.map((f) => (f.id === id ? { ...f, riskAcknowledged: !f.riskAcknowledged } : f)), changedIds: [id] }
}
/** Assign an unscheduled card to a dev's lane; if datable, append after the
 *  lane's last scheduled card (working day after its est completion). */
export function assignToLane(fs: Feature[], id: number, dev: string, blockouts: Blockout[]): MutResult {
  const target = fs.find((x) => x.id === id)
  if (!target || !dev) return { features: fs }
  setScheduleBlockouts(blockouts)
  let startVal = target.start
  const datable = !!(target.deadline && hasEstimate(target))
  if (datable) {
    const lane = fs.filter((x) => x.id !== id && x.dev === dev && datedCard(x) && !x.multiStage).sort(laneSort)
    const last = lane[lane.length - 1]
    startVal = last && last.estCompletion ? toISO(nextWorkingDay(parseISO(last.estCompletion), dev)) : todayISO()
  }
  return {
    features: fs.map((f) => (f.id === id ? { ...f, dev, start: startVal } : f)),
    toast: '“' + target.title + '” assigned to ' + (DL[dev] || dev) + (datable ? '’s timeline.' : ' — add a deadline & estimate to schedule it.'),
    changedIds: [id],
  }
}

/** v2 §2: the scheduled stage units on one dev's lane (for Calendar/Roadmap). */
export interface StageUnit { f: Feature; s: import('./types').Stage; i: number }
export function stageUnits(features: Feature[], dev: string): StageUnit[] {
  const out: StageUnit[] = []
  features.forEach((f) => {
    if (!f.multiStage) return
    f.stages.forEach((s, i) => {
      if ((s.dev || f.dev) === dev && s.schedStart && s.estCompletion) out.push({ f, s, i })
    })
  })
  return out
}
export function stageLabel(f: Feature, i: number): string {
  const s = f.stages[i]
  return f.title + ' · Stage ' + (i + 1) + (s && s.name ? ': ' + s.name : '')
}

/* ── v2 §3: the SCHEDULE action (verify §71). Estimate required so it always
 * yields a projected completion + a slot; the start pins (consistent with
 * "typing a start pins"). Staged cards schedule per-stage — assignee stays
 * per-stage (locked decision). ── */
export function previewCompletion(startISO: string, time: string, dev: string, blockouts: Blockout[]): string {
  if (!startISO || !time || !(parseFloat(time) > 0)) return ''
  setScheduleBlockouts(blockouts)
  return toISO(addWorkingDays(snapWorking(parseISO(startISO), dev), parseFloat(time), dev))
}
export function confirmSchedule(fs: Feature[], id: number, dev: string, startISO: string, time: string): MutResult {
  if (!time) return { features: fs, toast: 'Please set an estimated dev time — it’s needed to work out the completion date and the scheduled slot.' }
  if (!startISO) return { features: fs, toast: 'Please pick a start date.' }
  let title = ''
  const next = fs.map((f) => {
    if (f.id !== id) return f
    title = f.title
    const eff = (parseFloat(time) < 0.5 ? 'quickwin' : parseFloat(time) <= 2 ? 'low' : parseFloat(time) <= 5 ? 'medium' : 'high') as Feature['eff']
    return { ...f, dev, start: startISO, pinnedStart: true, time, eff }
  })
  return { features: next, changedIds: [id], toast: '“' + title + '” scheduled' + (dev && DL[dev] ? ' to ' + DL[dev] : '') + ' — projected completion set.' }
}
export interface StagePick { sid: number; start: string; time?: string }
export function confirmScheduleStaged(fs: Feature[], id: number, picks: StagePick[]): MutResult {
  const f = fs.find((x) => x.id === id)
  if (!f) return { features: fs }
  const valid = picks.filter((p) => p.start)
  if (!valid.length) return { features: fs, toast: 'Tick at least one stage and give it a start date.' }
  const missing = valid.some((p) => {
    const sg = f.stages.find((x) => x.id === p.sid)
    return sg && !sg.time && !p.time
  })
  if (missing) return { features: fs, toast: 'Each scheduled stage needs an estimate.' }
  const next = fs.map((g) => {
    if (g.id !== id) return g
    const stages = g.stages.map((sg) => {
      const p = valid.find((x) => x.sid === sg.id)
      if (!p || sg.done) return sg
      return { ...sg, start: p.start, time: sg.time || p.time || sg.time }
    })
    const total = stages.reduce((t, sg) => { const n = parseFloat(sg.time); return t + (isNaN(n) ? 0 : n) }, 0)
    const time = total > 0 ? String(total) : ''
    return { ...g, stages, time: g.multiStage ? time : g.time }
  })
  return { features: next, changedIds: [id], toast: 'Stage(s) scheduled for “' + f.title + '”.' }
}
